import axios from 'axios';
import { Comic, ComicCreate, Person, Tag, Role, CustomList, CustomListCreate, CustomListUpdate, CustomListReorder, PublicListResponse, Language, MaintenanceData, MaintenanceDeleteRequest, MaintenanceDeleteResponse, ExportRequest, ExportStatusResponse, ImportStatusResponse, LLMConfiguration, LLMConfigurationCreate, LLMConfigurationUpdate, LLMConfigurationListResponse, ScrapeRequest, ScrapeResponse, UserSettings, UserSettingsUpdate, AvailableFields } from './types';
import { getWebroot } from './utils/webroot';

// Create API instance with dynamic base URL
function createApiInstance() {
  return axios.create({
    baseURL: '/api', // Will be updated dynamically
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export const api = createApiInstance();

// Request interceptor to update base URL with webroot and add auth token
api.interceptors.request.use((config) => {
  const webroot = getWebroot();
  
  // Update base URL with webroot
  const baseURL = webroot ? `${webroot}/api` : '/api';
  config.baseURL = baseURL;
  
  // Add auth token
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

export const getImageUrl = (imagePath?: string, useScaled: boolean = false): string => {
  if (!imagePath) return '';
  
  const webroot = getWebroot();
  
  // For scaled images, check if it's already in the scaled directory
  if (useScaled && !imagePath.includes('scaled/')) {
    // Try scaled version first - backend has optimized images in covers/scaled/
    return `${webroot}/images/scaled/${imagePath}`;
  }
  
  // Always add the /images/ prefix since database stores only filenames
  return `${webroot}/images/${imagePath}`;
};

// getProxyImageUrl removed - replaced with direct download mechanism in scraping flow

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't handle 401s for login or refresh requests to prevent recursion
      if (originalRequest.headers?.['X-Login-Request'] === 'true' || 
          originalRequest.headers?.['X-Refresh-Request'] === 'true') {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh', { refresh_token: refreshToken }, {
            headers: {
              'X-Refresh-Request': 'true' // Mark as refresh request to prevent recursive handling
            }
          });
          const { access_token, refresh_token: new_refresh_token } = response.data;
          
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', new_refresh_token);
          
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          
          processQueue(null, access_token);
          
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          
          // Force authentication state refresh without full page reload
          // This will trigger the ProtectedRoute to show login component
          const event = new CustomEvent('auth-invalidated');
          window.dispatchEvent(event);
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token available, clear any stale access token
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        // Force authentication state refresh without full page reload
        const event = new CustomEvent('auth-invalidated');
        window.dispatchEvent(event);
      }
    }
    
    return Promise.reject(error);
  }
);

export const getComics = async (customOrder: boolean = false): Promise<Comic[]> => {
  const response = await api.get('/comics/', {
    params: customOrder ? { custom_order: true } : {}
  });
  return response.data;
};

export const getComic = async (id: number): Promise<Comic> => {
  const response = await api.get(`/comics/${id}`);
  return response.data;
};

export const createComic = async (comic: ComicCreate): Promise<Comic> => {
  const response = await api.post('/comics/', comic);
  return response.data;
};

export const updateComic = async (id: number, comic: ComicCreate): Promise<Comic> => {
  const response = await api.put(`/comics/${id}`, comic);
  return response.data;
};

export const deleteComic = async (id: number): Promise<void> => {
  await api.delete(`/comics/${id}`);
};

export const getPeople = async (): Promise<Person[]> => {
  const response = await api.get('/people/');
  return response.data;
};

export const searchPeople = async (query: string, limit: number = 10): Promise<Person[]> => {
  const response = await api.get('/people/search', { params: { query, limit } });
  return response.data;
};

export const createPerson = async (person: { name: string }): Promise<Person> => {
  const response = await api.post('/people/', person);
  return response.data;
};

export const getLoanContacts = async (): Promise<Person[]> => {
  const response = await api.get('/loan-contacts/');
  return response.data;
};

export const searchLoanContacts = async (query: string, limit: number = 10): Promise<Person[]> => {
  const response = await api.get('/loan-contacts/search', { params: { query, limit } });
  return response.data;
};

export const createLoanContact = async (loanContact: { name: string }): Promise<Person> => {
  const response = await api.post('/loan-contacts/', loanContact);
  return response.data;
};

export const getTags = async (): Promise<Tag[]> => {
  const response = await api.get('/tags/');
  return response.data;
};

export const searchTags = async (query: string, limit: number = 10): Promise<Tag[]> => {
  const response = await api.get('/tags/search', { params: { query, limit } });
  return response.data;
};

export const createTag = async (tag: { name: string }): Promise<Tag> => {
  const response = await api.post('/tags/', tag);
  return response.data;
};

export const getRoles = async (): Promise<Role[]> => {
  const response = await api.get('/roles/');
  return response.data;
};

export const searchRoles = async (query: string, limit: number = 10): Promise<Role[]> => {
  const response = await api.get('/roles/search', { params: { query, limit } });
  return response.data;
};

export const createRole = async (role: { name: string }): Promise<Role> => {
  const response = await api.post('/roles/', role);
  return response.data;
};

export const getCustomLists = async (): Promise<CustomList[]> => {
  const response = await api.get('/custom-lists/');
  return response.data;
};

export const getCustomList = async (id: number): Promise<CustomList> => {
  const response = await api.get(`/custom-lists/${id}`);
  return response.data;
};

export const createCustomList = async (list: CustomListCreate): Promise<CustomList> => {
  const response = await api.post('/custom-lists/', list);
  return response.data;
};

export const updateCustomList = async (id: number, list: CustomListUpdate): Promise<CustomList> => {
  const response = await api.put(`/custom-lists/${id}`, list);
  return response.data;
};

export const deleteCustomList = async (id: number): Promise<void> => {
  await api.delete(`/custom-lists/${id}`);
};

export const reorderListComics = async (listId: number, reorderData: CustomListReorder): Promise<void> => {
  await api.post(`/custom-lists/${listId}/reorder`, reorderData);
};

export const reorderUserComics = async (reorderData: CustomListReorder): Promise<void> => {
  await api.post('/comics/reorder', reorderData);
};

export const copyCustomList = async (listId: number, name: string): Promise<CustomList> => {
  const response = await api.post(`/custom-lists/${listId}/copy`, { name });
  return response.data;
};

export const generateCustomListShareToken = async (listId: number): Promise<CustomList> => {
  const response = await api.post(`/custom-lists/${listId}/generate-token`);
  return response.data;
};

export const regenerateCustomListShareToken = async (listId: number): Promise<CustomList> => {
  const response = await api.post(`/custom-lists/${listId}/regenerate-token`);
  return response.data;
};

export const getPublicList = async (shareToken: string): Promise<PublicListResponse> => {
  const response = await api.get(`/public-lists/${shareToken}`);
  return response.data;
};

export const uploadCover = async (file: File): Promise<{ filename: string; path: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload-cover/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const downloadCover = async (url: string): Promise<{ filename: string; path: string }> => {
  const response = await api.get('/download-cover/', { params: { url } });
  return response.data;
};

export const cleanupCoverFile = async (filename: string): Promise<{ message: string; deleted_files: string[] }> => {
  const response = await api.delete(`/cleanup-cover/${filename}`);
  return response.data;
};


export const getLanguages = async (): Promise<Language[]> => {
  const response = await api.get('/languages/');
  return response.data;
};

export const searchLanguages = async (query: string, limit: number = 10): Promise<Language[]> => {
  const response = await api.get('/languages/search', { params: { query, limit } });
  return response.data;
};

export const createLanguage = async (language: { name: string; code?: string }): Promise<Language> => {
  const response = await api.post('/languages/', language);
  return response.data;
};

export const getFilteredLanguages = async (query: string = '', limit: number = 10): Promise<Language[]> => {
  try {
    const settings = await getUserSettings();
    const availableLanguages = settings.available_languages;
    
    // If user has selected specific languages, filter by those
    if (availableLanguages && availableLanguages.length > 0) {
      const allLanguages = await searchLanguages(query, 100); // Get more results to filter
      return allLanguages.filter(lang => 
        availableLanguages.includes(lang.code || '') || 
        (query && lang.name.toLowerCase().includes(query.toLowerCase()))
      ).slice(0, limit);
    }
    
    // Fallback to all languages if no preferences set
    return await searchLanguages(query, limit);
  } catch (error) {
    // Fallback to normal search if settings fetch fails
    return await searchLanguages(query, limit);
  }
};

export const searchSeries = async (query: string, limit: number = 10): Promise<string[]> => {
  const response = await api.get('/series/search', { params: { query, limit } });
  return response.data.map((item: { series: string }) => item.series);
};

export const login = async (username: string, password: string): Promise<{ access_token: string; refresh_token: string; token_type: string }> => {
  const formData = new FormData();
  formData.append('username', username);
  formData.append('password', password);
  
  const response = await api.post('/auth/login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Login-Request': 'true', // Mark this as a login request to prevent reload on 401
    },
  });
  
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');
};

export const checkUsersExist = async (): Promise<{ has_users: boolean }> => {
  // Create a temporary API instance without auth headers for this call
  const tempApi = createApiInstance();
  tempApi.interceptors.request.use((config) => {
    const webroot = getWebroot();
    const baseURL = webroot ? `${webroot}/api` : '/api';
    config.baseURL = baseURL;
    // Don't add auth token for this call
    return config;
  });
  
  const response = await tempApi.get('/auth/has-users');
  return response.data;
};

export const setupFirstUser = async (username: string, password: string): Promise<{ id: number; username: string; is_active: boolean; created_at: string }> => {
  // Create a temporary API instance without auth headers for this call
  const tempApi = createApiInstance();
  tempApi.interceptors.request.use((config) => {
    const webroot = getWebroot();
    const baseURL = webroot ? `${webroot}/api` : '/api';
    config.baseURL = baseURL;
    // Don't add auth token for this call
    return config;
  });
  
  const response = await tempApi.post('/auth/setup-first-user', {
    username,
    password
  });
  return response.data;
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// Maintenance API functions
export const getMaintenanceData = async (): Promise<MaintenanceData> => {
  const response = await api.get('/maintenance/');
  return response.data;
};

export const deleteUnusedEntities = async (request: MaintenanceDeleteRequest): Promise<MaintenanceDeleteResponse> => {
  const response = await api.post('/maintenance/delete', request);
  return response.data;
};

// Export/Import API functions
export const exportCollection = async (request: ExportRequest): Promise<ExportStatusResponse> => {
  const response = await api.post('/export', request);
  return response.data;
};

export const downloadExport = async (filename: string): Promise<Blob> => {
  const response = await api.get(`/export/download/${filename}`, {
    responseType: 'blob',
  });
  return response.data;
};

export const importCollection = async (
  file: File,
  overwriteExisting: boolean = false,
  importImages: boolean = true,
  importCustomLists: boolean = true
): Promise<ImportStatusResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('overwrite_existing', overwriteExisting.toString());
  formData.append('import_images', importImages.toString());
  formData.append('import_custom_lists', importCustomLists.toString());

  const response = await api.post('/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};


// LLM Configuration API functions
export const getLLMConfigurations = async (): Promise<LLMConfigurationListResponse> => {
  const response = await api.get('/llm-configurations');
  // Backend returns array directly, wrap it in expected format
  return {
    configurations: response.data,
    total: response.data.length
  };
};

export const getLLMConfiguration = async (id: number): Promise<LLMConfiguration> => {
  const response = await api.get(`/llm-configurations/${id}`);
  return response.data;
};

export const createLLMConfiguration = async (config: LLMConfigurationCreate): Promise<LLMConfiguration> => {
  const response = await api.post('/llm-configurations', config);
  return response.data;
};

export const updateLLMConfiguration = async (id: number, config: LLMConfigurationUpdate): Promise<LLMConfiguration> => {
  const response = await api.put(`/llm-configurations/${id}`, config);
  return response.data;
};

export const deleteLLMConfiguration = async (id: number): Promise<void> => {
  await api.delete(`/llm-configurations/${id}`);
};

export const setDefaultLLMConfiguration = async (configurationId: number): Promise<void> => {
  const response = await api.post(`/llm-configurations/${configurationId}/set-default`);
  return response.data;
};

// Scrape API function
export const scrapeComicData = async (request: ScrapeRequest): Promise<ScrapeResponse> => {
  const response = await api.post('/llm/scrape-comic', request);
  return response.data;
};

// User Settings API functions
export const getUserSettings = async (): Promise<UserSettings> => {
  const response = await api.get('/user/settings');
  return response.data;
};

export const updateUserSettings = async (settings: UserSettingsUpdate): Promise<UserSettings> => {
  const response = await api.put('/user/settings', settings);
  return response.data;
};

export const getAvailableFields = async (): Promise<AvailableFields> => {
  const response = await api.get('/user/available-fields');
  return response.data;
};

